import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Tooltip, Button, Modal, Popconfirm } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import Auxiliary from "../../../util/Auxiliary";
import ModuleBreadcrumb from "../../../components/Common/ModuleBreadcrumb";
import AppTable from "../../../components/Common/AppTable";
import {
  getUser,
  getTotalUsers,
  getuserdetailSelector,
} from "./../../../appRedux/selectors/User";
import { useDispatch, useSelector } from "react-redux";
import {
  requestUserDetails,
  requestUserRemove,
  requestUserDetailById,
  successUserDetailById,
} from "../../../appRedux/actions/User";
import { useHistory, useLocation } from "react-router-dom";
import SearchBox from "../../../components/Common/AppTable/SearchBox";
import { debounce } from "lodash";
import UserDetails from "./UserDetails";

const ViewUser = () => {
  const [userListCount, setUserListCount] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchBarText, setSearchBarText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const total = useSelector(getTotalUsers);
  const Users = useSelector(getUser);
  const userDetail = useSelector(getuserdetailSelector);
  const dispatch = useDispatch();
  const history = useHistory();

  const pageCount = () => {
    if (userListCount === 10) {
      switch (currentPage) {
        case 1:
          return 1;
        case 2:
          return 11;
        case 3:
          return 21;
        case 4:
          return 31;
        case 5:
          return 41;
        case 6:
          return 51;
        case 7:
          return 61;
        case 8:
          return 71;
        case 9:
          return 81;
        case 10:
          return 91;
        case 11:
          return 101;
        default:
          return 1;
      }
    } else {
      switch (currentPage) {
        case 1:
          return 1;
        case 2:
          return 21;
        case 3:
          return 41;
        case 4:
          return 61;
        case 5:
          return 81;
        case 6:
          return 101;
        default:
          return 1;
      }
    }
  };

  const usersData = useMemo(() => {
    if (Users && Users.length) {
      return Users.map((a, i) => {
        return {
          ...a,
          key: a.key,
          birthdate: a?.birthdate.slice(0, 10),
          no: i + pageCount(),
        };
      });
    }
    return [];
  }, [Users, pageCount]);

  const getFromQueryParam =
    new URLSearchParams(useLocation()?.search)?.get("from") || null;

  // Initial Params for fetching users
  const initialParams = useMemo(() => {
    if (userListCount && getFromQueryParam === "add") {
      return {
        ...userListCount,
      };
    }
    return {
      page: 1,
      limit: 10,
      q: searchBarText,
    };
  }, []);

  //Fetch User Details
  useEffect(() => {
    dispatch(requestUserDetails(initialParams));
  }, [initialParams]);

  // On Search it will fetch the User Name
  const onSearchHandler = (values) => {
    const params = {
      ...initialParams,
      page: 1,
      q: values ? values : "",
    };
    dispatch(requestUserDetails(params));
  };

  // Add Debounce to search for performance and prevent to extra call
  const onSearchDebounce = useCallback(
    debounce((text) => {
      onSearchHandler(text);
      setSearchBarText(text);
    }, 700),
    []
  );

  const onSearchTextChangeHandler = (text) => {
    onSearchDebounce(text);
  };

  const userDeleteHandler = (id) => {
    dispatch(requestUserRemove({ id }));
  };

  const showModal = (id) => {
    setIsModalVisible(true);
    dispatch(requestUserDetailById(id));
  };

  const onCancel = () => {
    setIsModalVisible(false);
    dispatch(successUserDetailById(null));
  };

  const columns = useMemo(() => {
    return [
      {
        title: "No",
        dataIndex: "no",
      },
      {
        title: "Name",
        dataIndex: "user_name",
        width: 220,
      },
      {
        title: "Mobile",
        dataIndex: "mobile",
      },
      {
        title: "Address",
        dataIndex: "address",
      },
      {
        title: "Action",
        width: 170,
        render: (value, row) => {
          return (
            <span>
              <Tooltip title="View Database details">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => showModal(row._id)}
                />
              </Tooltip>
              <Tooltip title="Edit">
                <Button
                  className="action-btn"
                  icon={<EditOutlined />}
                  type="primary"
                  size="small"
                  onClick={() => history.push(`/database/edit/${row._id}`)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Are you sure to Delete?"
                  onConfirm={() => userDeleteHandler(row._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    className="action-btn"
                    icon={<DeleteOutlined />}
                    type="danger"
                    size="small"
                  />
                </Popconfirm>
              </Tooltip>
            </span>
          );
        },
      },
    ];
  }, []);

  return (
    <Auxiliary>
      <ModuleBreadcrumb
        title="Nikol Bal-Sabha Database"
        breadcrumbs={["Database", "View"]}
        links={["/database", "/databse", ""]}
      />
      <Row className="gx-profile-content">
        <Col span={24}>
          <div className="gx-card gx-card-body">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <SearchBox
                placeholder="Search..."
                value={userListCount?.q || ""}
                // length={restaurantsData?.length}
                styleName="gx-lt-icon-search -bar-lg"
                searchText={searchText}
                setSearchText={setSearchText}
                onChange={(e) => onSearchTextChangeHandler(e)}
              />
            </div>
            <AppTable
              columns={columns}
              dataSource={usersData}
              pagination={{
                pageSize: userListCount,
                total: total || 0,
                pageSizeOptions: ["10", "20"],
                showSizeChanger: true,
                onChange: (page, limit) => {
                  dispatch(
                    requestUserDetails({
                      page,
                      limit,
                      q: searchBarText,
                    })
                  );
                  setUserListCount(limit);
                  setCurrentPage(page);
                },
              }}
            />
          </div>
        </Col>
      </Row>
      <Modal
        title="Person Details"
        visible={isModalVisible}
        onCancel={onCancel}
        cancelButtonProps={{ style: { display: "none" } }}
        okButtonProps={{ style: { display: "none" } }}
      >
        {userDetail && <UserDetails userDetail={userDetail} />}
      </Modal>
    </Auxiliary>
  );
};

export default ViewUser;
